import { ConnectionState, Room } from 'livekit-client'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type UseChatResult, useChat } from '../chat/use-chat'
import { RT_TOPIC, type RtRole, type StatusMsg, encodeRtMsg } from '../lib/rt-protocol'

/**
 * App-level rt connection context (spec v2.11 — pre-flight chat).
 *
 * The /join page connects to the LiveKit room the moment it has a token —
 * phase='preflight': NOTHING is published (no mic, no camera) and NOTHING is
 * subscribed (autoSubscribe:false ⇒ the program feed stays unwatched, the
 * bandwidth probes run clean). What the preflight connection DOES carry:
 *
 *   - chat (data channel topic 'chat') — guest can talk to the studio while
 *     the checks are still running; a blocking POOR auto-pings the engineer
 *   - a 5s status heartbeat {t:'status', phase:'preflight', grades:{…}} so
 *     the studio Available Connections row streams the medical report live
 *
 * The chat HOOK INSTANCE lives here (provider mounted at App level), so its
 * message state survives the /join → /room transition: RoomPage re-wires the
 * same instance onto the in-room Room via registerRoom() and the transcript
 * carries over seamlessly (v2.11 #5).
 */

export type PreflightGrades = Record<string, string>

export interface RtConnectionValue {
  /** App-lifetime chat — same instance on /join (preflight) and /room. */
  chat: UseChatResult
  /** Connected preflight-phase Room (null when idle / after room takeover). */
  preflightRoom: Room | null
  /** Connect for phase='preflight' (idempotent per token). */
  connectPreflight: (opts: { url: string; token: string; role: RtRole; name: string }) => void
  /** Tear the preflight connection down (leaving /join or entering /room).
   *  Resolves once the LiveKit signal socket is actually closed — await it
   *  before connecting elsewhere with the SAME identity (duplicate-identity
   *  kicks otherwise race the teardown). */
  disconnectPreflight: () => Promise<void>
  /** Stream grade updates → immediate {t:'status', phase:'preflight'} send. */
  setPreflightGrades: (grades: PreflightGrades) => void
  /** RoomPage takeover: swap the chat wiring onto the in-room Room. */
  registerRoom: (
    room: Room | null,
    identity: string,
    role: RtRole | 'unknown',
    name: string,
  ) => void
}

const Ctx = createContext<RtConnectionValue | null>(null)

export function RtConnectionProvider({ children }: { children: ReactNode }) {
  const [wiring, setWiring] = useState<{
    room: Room | null
    identity: string
    role: RtRole | 'unknown'
    name: string
  }>({ room: null, identity: 'local', role: 'unknown', name: '' })

  const chat = useChat({
    room: wiring.room,
    localIdentity: wiring.identity,
    localRole: wiring.role,
    localName: wiring.name,
  })

  const pfRoomRef = useRef<Room | null>(null)
  const pfTokenRef = useRef('')
  const pfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pfInfoRef = useRef<{ role: RtRole; name: string } | null>(null)
  const gradesRef = useRef<PreflightGrades | null>(null)
  const [preflightRoom, setPreflightRoom] = useState<Room | null>(null)

  const publishPreflightStatus = useCallback(() => {
    const room = pfRoomRef.current
    const info = pfInfoRef.current
    if (!room || !info || room.state !== ConnectionState.Connected) return
    const msg: StatusMsg = {
      t: 'status',
      role: info.role,
      name: info.name,
      ready: false,
      phase: 'preflight',
      ...(gradesRef.current ? { grades: gradesRef.current } : {}),
    }
    void room.localParticipant
      .publishData(encodeRtMsg(msg), { reliable: true, topic: RT_TOPIC })
      .catch(() => undefined)
  }, [])

  const disconnectPreflight = useCallback((): Promise<void> => {
    if (pfTimerRef.current) {
      clearInterval(pfTimerRef.current)
      pfTimerRef.current = null
    }
    const room = pfRoomRef.current
    pfRoomRef.current = null
    pfTokenRef.current = ''
    pfInfoRef.current = null
    gradesRef.current = null
    setPreflightRoom(null)
    if (room) {
      setWiring((w) => (w.room === room ? { ...w, room: null } : w))
      return room.disconnect().then(
        () => undefined,
        () => undefined,
      )
    }
    return Promise.resolve()
  }, [])

  const connectPreflight = useCallback(
    (opts: { url: string; token: string; role: RtRole; name: string }) => {
      if (pfTokenRef.current === opts.token && pfRoomRef.current) return // already on it
      void disconnectPreflight()
      // NO auto-reconnect: when the guest moves /join → /room, the in-room
      // connection reuses the SAME LiveKit identity and the server kicks this
      // preflight participant. A default-policy Room would try to reconnect
      // and kick the in-room connection right back — an endless identity
      // tug-of-war. A kicked/closed preflight connection must stay dead.
      const room = new Room({ reconnectPolicy: { nextRetryDelayInMs: () => null } })
      pfRoomRef.current = room
      pfTokenRef.current = opts.token
      pfInfoRef.current = { role: opts.role, name: opts.name }
      void (async () => {
        try {
          // phase='preflight': data channels only — publish nothing, subscribe
          // to nothing (the program feed must not pollute the bandwidth test).
          await room.connect(opts.url, opts.token, { autoSubscribe: false })
        } catch {
          if (pfRoomRef.current === room) {
            pfRoomRef.current = null
            pfTokenRef.current = ''
            pfInfoRef.current = null
          }
          return
        }
        if (pfRoomRef.current !== room) {
          // superseded while connecting (user already moved on)
          void room.disconnect().catch(() => undefined)
          return
        }
        setPreflightRoom(room)
        setWiring({
          room,
          identity: room.localParticipant.identity || 'local',
          role: opts.role,
          name: opts.name,
        })
        publishPreflightStatus()
        pfTimerRef.current = setInterval(publishPreflightStatus, 5000)
      })()
    },
    [disconnectPreflight, publishPreflightStatus],
  )

  const setPreflightGrades = useCallback(
    (grades: PreflightGrades) => {
      gradesRef.current = grades
      publishPreflightStatus() // stream every grade as it lands (v2.11 #2)
    },
    [publishPreflightStatus],
  )

  const registerRoom = useCallback(
    (room: Room | null, identity: string, role: RtRole | 'unknown', name: string) => {
      if (room) disconnectPreflight()
      setWiring({ room, identity, role, name })
    },
    [disconnectPreflight],
  )

  const value = useMemo(
    () => ({
      chat,
      preflightRoom,
      connectPreflight,
      disconnectPreflight,
      setPreflightGrades,
      registerRoom,
    }),
    [chat, preflightRoom, connectPreflight, disconnectPreflight, setPreflightGrades, registerRoom],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useRtConnection(): RtConnectionValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useRtConnection must be used inside RtConnectionProvider')
  return v
}
